import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Highcharts from "highcharts";
import { useIntl } from "react-intl";

import DateRangeType from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import { LoadingSpinner } from "@mapmycustomers/ui";

import CardType from "@app/enum/dashboard/CardType";
import { DateRange, DateSubRange } from "@app/enum/dashboard/dateRanges";
import PreviewType from "@app/enum/dashboard/PreviewType";
import BaseCard from "@app/scene/dashboard/components/cards/common/BaseCard";
import Count from "@app/scene/dashboard/components/cards/common/Count";
import Menu from "@app/scene/dashboard/components/cards/common/Menu";
import {
  fetchClosedStageDealAmountCardData,
  hideClosedStageDealAmountDrillDown,
  showClosedStageDealAmountDrillDown,
} from "@app/scene/dashboard/store/actions";
import ClosedStageDealAmountCardData from "@app/scene/dashboard/store/cards/closedStageDealAmount/ClosedStageDealAmountCardData";
import { getClosedStageDealAmountDrillDownConfiguration } from "@app/scene/dashboard/store/cards/closedStageDealAmount/selectors";
import CardContentProps from "@app/scene/dashboard/types/CardContentProps";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import getSampleChartData from "@app/scene/dashboard/utils/getSampleChartData";
import { getCurrentUser } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import ClosedStageDealAmountCardConfiguration from "@app/types/dashboard/cards/closedStageDealAmount/ClosedStageDealAmountCardConfiguration";
import ClosedStageDealAmountViewState from "@app/types/dashboard/cards/closedStageDealAmount/ClosedStageDealAmountViewState";

import GoalProgress from "../../common/GoalProgress";
import TemporaryConfigurationAlert from "../../common/TemporaryConfigurationAlert";

import styles from "./CardContentComponent.module.scss";
import Chart from "./Chart";
import DrillDown from "./DrillDown";
import messages from "./messages";
import Subtitle from "./Subtitle";
import Title from "./Title";

const SAMPLE_CONFIGURATION: ClosedStageDealAmountCardConfiguration = {
  criteria: {
    adjustedAmount: false,
    dealStageIds: [],
  },
  dateRange: {
    range: DateRange.WEEK,
    subRange: DateSubRange.THIS,
  },
  goal: {
    enabled: true,
    target: 1000,
  },
  preferences: {
    cumulative: true,
    showChart: true,
    showRankPosition: true,
    showTrend: true,
  },
};

interface Props
  extends CardContentProps<ClosedStageDealAmountCardConfiguration, ClosedStageDealAmountViewState> {
  currentUser: User;
  drillDownVisible: boolean;
  onFetchData?: typeof fetchClosedStageDealAmountCardData;
  onHideDrillDown: () => void;
  onShowDrillDown: typeof showClosedStageDealAmountDrillDown.request;
}

const CardContentComponent: React.FC<Props> = ({
  configuration,
  currentUser,
  draggable,
  drillDownVisible,
  fullWidth,
  onChangeConfiguration,
  onChangeView,
  onCopy,
  onEdit,
  onFetchData,
  onHideDrillDown,
  onRemove,
  onResetTemporaryConfiguration,
  onShowDrillDown,
  preview,
  scope,
  temporaryConfiguration,
}) => {
  const intl = useIntl();

  const sampleData: ClosedStageDealAmountCardData = useMemo(
    () => ({
      count: 1340,
      history: getSampleChartData().map(({ date, value }) => ({ count: value, date })),
      previousCount: 1290,
      rank: 2,
    }),
    []
  );

  const [data, setData] = useState<ClosedStageDealAmountCardData | undefined>(
    preview === PreviewType.SAMPLE_DATA ? sampleData : undefined
  );

  const finalConfiguration =
    preview === PreviewType.SAMPLE_DATA
      ? SAMPLE_CONFIGURATION
      : temporaryConfiguration ?? configuration;

  useEffect(
    () => {
      if (preview !== PreviewType.SAMPLE_DATA) {
        onFetchData?.({ callback: setData, configuration: finalConfiguration, scope });
      }
    },
    // Removing scope from deps because scope never changes for a card on a board.
    // When scope changes, that is a new card.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalConfiguration, onFetchData, preview]
  );

  const [showGoal, setShowGoal] = useState<number | undefined>(undefined);
  const [showPrevious, setShowPrevious] = useState<boolean>(false);

  const handleViewData = useCallback(
    () => onShowDrillDown?.({ configuration: finalConfiguration, scope }),
    [finalConfiguration, onShowDrillDown, scope]
  );

  const handleChartPointClick = useCallback(
    (customDateRange: DateRangeType) =>
      onShowDrillDown?.({ configuration: finalConfiguration, customDateRange, scope }),
    [finalConfiguration, onShowDrillDown, scope]
  );

  const handleChangeTemporarily = useCallback(
    (configuration: Partial<ClosedStageDealAmountCardConfiguration>) => {
      onChangeConfiguration?.(configuration, true);
    },
    [onChangeConfiguration]
  );

  const handleSaveTemporaryConfig = useCallback(() => {
    if (temporaryConfiguration) {
      onChangeConfiguration?.(temporaryConfiguration, false);
      onResetTemporaryConfiguration?.();
    }
  }, [temporaryConfiguration, onResetTemporaryConfiguration, onChangeConfiguration]);

  const handleChangeFullWidth = useCallback(
    (fullWidth: boolean) => {
      onChangeView?.({ fullWidth });
    },
    [onChangeView]
  );

  useEffect(() => {
    // charts don't handle height changes very well, and showing a TemporaryConfigurationAlert
    // does change their height. So we're force-reflowing them to fit better. Wish we could only
    // reflow the ones we need...
    Highcharts.charts.forEach((chart) => chart?.reflow());
  }, [temporaryConfiguration, configuration.goal?.enabled, fullWidth]);

  const configurationIsEditable = !!canEditBoard(currentUser, scope, "editCardConfiguration");

  return (
    <BaseCard
      contentClassName={styles.container}
      draggable={draggable}
      menu={
        <Menu
          expanded={fullWidth}
          onCopy={onCopy}
          onEdit={onEdit}
          onRemove={onRemove}
          onResize={preview !== undefined ? undefined : handleChangeFullWidth}
          onViewData={preview !== undefined ? undefined : handleViewData}
        />
      }
      subtitle={<Subtitle rank={data?.rank} scope={scope} />}
      title={
        <Title
          customTitle={finalConfiguration.customTitle}
          dateRange={finalConfiguration.dateRange}
          dealStageIds={finalConfiguration.criteria.dealStageIds}
          editable={!!onChangeConfiguration}
          onChangeConfiguration={handleChangeTemporarily}
          onTrendHover={setShowPrevious}
          scope={scope}
          trend={
            data && data.previousCount
              ? (data.count - data.previousCount) / data.previousCount
              : undefined
          }
        />
      }
      tooltip={
        <Title
          dateRange={finalConfiguration.dateRange}
          dealStageIds={finalConfiguration.criteria.dealStageIds}
          editable={false}
          scope={scope}
        />
      }
    >
      {!data ? (
        <LoadingSpinner global />
      ) : (
        <>
          <Count className={styles.count} count={data.count} currency />
          {temporaryConfiguration ? (
            <TemporaryConfigurationAlert
              onSave={configurationIsEditable ? handleSaveTemporaryConfig : undefined}
              onUndo={onResetTemporaryConfiguration}
            />
          ) : null}
          {data.history && (
            <Chart
              className={styles.chart}
              dateRange={finalConfiguration.dateRange}
              history={data.history}
              onPointClick={handleChartPointClick}
              previousHistory={showPrevious ? data.previousHistory : undefined}
              showGoal={showGoal}
            />
          )}
          {!!finalConfiguration.goal?.enabled && (
            <GoalProgress
              currencyType
              goalConfiguration={finalConfiguration.goal}
              label={intl.formatMessage(messages.goalLabel)}
              onMouseEnter={setShowGoal}
              onMouseLeave={setShowGoal}
              scope={scope}
              type={CardType.CLOSED_STAGE_DEAL_AMOUNT}
              value={data.count}
            />
          )}
        </>
      )}
      {drillDownVisible && (
        <DrillDown
          configuration={finalConfiguration}
          onEdit={onEdit}
          onHide={onHideDrillDown}
          scope={scope}
        />
      )}
    </BaseCard>
  );
};

const mapStateToProps = (
  state: RootState,
  props: CardContentProps<ClosedStageDealAmountCardConfiguration>
) => ({
  currentUser: getCurrentUser(state)!,
  drillDownVisible:
    getClosedStageDealAmountDrillDownConfiguration(state) ===
    (props.temporaryConfiguration ?? props.configuration),
});

const mapDispatchToProps = {
  onFetchData: fetchClosedStageDealAmountCardData,
  onHideDrillDown: hideClosedStageDealAmountDrillDown,
  onShowDrillDown: showClosedStageDealAmountDrillDown.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardContentComponent);
